import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';

// Two-step inline confirm for destructive actions (replaces window.confirm).
// First click arms it ("확인?" state); a second click within 2.5s fires onConfirm.
interface InlineConfirmProps {
  onConfirm: () => void;
  label?: string;
  confirmLabel?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

export function InlineConfirm({
  onConfirm,
  label = 'Delete',
  confirmLabel = 'Click again to confirm',
  disabled = false,
  ariaLabel,
}: InlineConfirmProps) {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disarm = useCallback(() => {
    setArmed(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (disabled) return;
      if (!armed) {
        setArmed(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setArmed(false), 2500);
        return;
      }
      disarm();
      onConfirm();
    },
    [armed, disabled, disarm, onConfirm],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      onBlur={disarm}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      className={`inline-flex items-center gap-1 whitespace-nowrap text-[11px] px-2.5 py-1 rounded-lg font-semibold
        cursor-pointer transition-all duration-150 disabled:opacity-50 border active:scale-[0.97]
        ${armed ? 'bg-error text-white border-error shadow-pop' : 'bg-error-bg text-error border-error-border hover:bg-error/10'}`}
    >
      {armed ? <AlertTriangle className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
      <span>{armed ? confirmLabel : label}</span>
    </button>
  );
}
